import _ from 'lodash';
import { nanoid } from 'nanoid';
import { SESSION_PROPERTIES, SESSION_TIMEOUT, ANALYTICS_STORAGE_KEYS } from 'shared/constants';
import mixpanel from 'mixpanel-browser';

class AnalyticsSession {
  constructor() {
    this._listeners = []; // List of event listeners for cleanup
    this._lastSessionId = null; // for cross-tab session tracking
  }

  getSessionId() {
    return localStorage.getItem(ANALYTICS_STORAGE_KEYS.SESSION_ID);
  }

  setSessionId(value) {
    localStorage.setItem(ANALYTICS_STORAGE_KEYS.SESSION_ID, value);
  }

  getLastActivityTime() {
    return parseInt(localStorage.getItem(ANALYTICS_STORAGE_KEYS.LAST_ACTIVITY), 10) || 0;
  }

  setLastActivityTime(value) {
    localStorage.setItem(ANALYTICS_STORAGE_KEYS.LAST_ACTIVITY, value.toString());
  }

  // This helps to avoid excessive access to localStorage and will ensure
  // that the last activity is updated at least every 3 seconds
  updateActivity = _.debounce(() => this._updateActivity(), 500, { leading: true, maxWait: 3000 });

  init() {
    this.updateActivity();
    this._setupActivityListeners();
  }

  _updateActivity() {
    const currentTime = Date.now();
    if (currentTime - this.getLastActivityTime() > SESSION_TIMEOUT) {
      this._generateNewSession();
    } else {
      this.setLastActivityTime(currentTime);
    }

    const sessionId = this.getSessionId();
    if (this._lastSessionId !== sessionId) {
      console.debug(`[Analytics] Session updated: ${this._lastSessionId} -> ${sessionId}`);

      this._lastSessionId = sessionId;
      // Register session ID with Mixpanel for analytics tracking
      mixpanel.register({ [SESSION_PROPERTIES.SESSION_ID]: sessionId });
    }
  }

  _generateNewSession() {
    const sessionId = nanoid();

    this.setSessionId(sessionId);
    this.setLastActivityTime(Date.now());

    console.debug('[Analytics] Generated a new session id:', sessionId);
  }

  _setupActivityListeners() {
    const activeEvents = ['keydown', 'touchstart', 'click', 'scroll'];
    activeEvents.forEach(event => {
      const listener = () => this.updateActivity();

      window.addEventListener(event, listener);
      this._listeners.push({ type: event, listener });
    });
  }

  destroy() {
    this._listeners.forEach(({ type, listener, target = window }) => {
      target.removeEventListener(type, listener);
    });

    localStorage.removeItem(ANALYTICS_STORAGE_KEYS.SESSION_ID);
    localStorage.removeItem(ANALYTICS_STORAGE_KEYS.LAST_ACTIVITY);
    localStorage.removeItem(ANALYTICS_STORAGE_KEYS.ACTIVITY_BROADCAST);

    this.updateActivity.cancel();

    console.debug('[Analytics] Session destroyed!');
  }
}

export default new AnalyticsSession();
